import { useState, useEffect } from "react";
import { useLang } from "../context/LanguageContext";
import { LanguageSelector } from "./LanguageSelector";
import { LastMinuteBanner } from "./LastMinuteBanner";
import { OwnerLoginModal } from "./OwnerLoginModal";
import { Button } from "./ui/button";
import { Menu, X, Lock } from "lucide-react";

const links = [
  { id: "gallery", key: "nav.gallery" },
  { id: "amenities", key: "nav.amenities" },
  { id: "location", key: "nav.location" },
  { id: "village", key: "nav.village" },
  { id: "pricing", key: "nav.pricing" },
  { id: "reviews", key: "nav.reviews" },
  { id: "booking", key: "nav.booking" },
  { id: "contact", key: "nav.contact" },
];

export const Header = () => {
  const { t } = useLang();
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [loginOpen, setLoginOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const go = (id) => {
    setMobileOpen(false);
    document.getElementById(id)?.scrollIntoView({ behavior: "smooth" });
  };

  return (
    <>
      <header
        className={`fixed top-0 left-0 right-0 z-50 transition-colors duration-300 ${
          scrolled ? "backdrop-blur-md bg-stone-50/85 border-b border-stone-200/60" : "bg-transparent"
        }`}
      >
        <LastMinuteBanner />
        <div className="mx-auto flex max-w-7xl items-center justify-between px-4 sm:px-8 py-3.5">
          <button
            data-testid="nav-brand-logo"
            onClick={() => window.scrollTo({ top: 0, behavior: "smooth" })}
            className={`font-serif text-xl sm:text-2xl font-semibold tracking-tight transition-colors ${
              scrolled ? "text-stone-900" : "text-white"
            }`}
          >
            The Cozy Nest
          </button>

          <nav className="hidden lg:flex items-center gap-7">
            {links.map((l) => (
              <button
                key={l.id}
                data-testid={`nav-link-${l.id}`}
                onClick={() => go(l.id)}
                className={`text-sm font-medium transition-colors hover:text-[#C85A32] ${
                  scrolled ? "text-stone-600" : "text-white/90"
                }`}
              >
                {t(l.key)}
              </button>
            ))}
          </nav>

          <div className="flex items-center gap-2.5">
            <LanguageSelector />
            <Button
              data-testid="nav-owner-login-button"
              onClick={() => setLoginOpen(true)}
              variant="outline"
              size="sm"
              className={`hidden sm:flex gap-1.5 border-stone-300/70 ${
                scrolled ? "bg-white/70 text-stone-700" : "bg-white/10 text-white border-white/30 hover:bg-white/20"
              }`}
            >
              <Lock className="h-3.5 w-3.5" />
              {t("nav.owner")}
            </Button>
            <button
              className={`lg:hidden ${scrolled ? "text-stone-800" : "text-white"}`}
              onClick={() => setMobileOpen((v) => !v)}
              data-testid="mobile-menu-toggle"
              aria-label="menu"
            >
              {mobileOpen ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
            </button>
          </div>
        </div>

        {mobileOpen && (
          <div className="lg:hidden border-t border-stone-200/60 bg-stone-50/95 backdrop-blur-md px-4 py-4 animate-fade-up">
            <nav className="flex flex-col gap-1">
              {links.map((l) => (
                <button
                  key={l.id}
                  data-testid={`mobile-nav-link-${l.id}`}
                  onClick={() => go(l.id)}
                  className="rounded-lg px-3 py-2.5 text-left text-base font-medium text-stone-700 hover:bg-stone-100"
                >
                  {t(l.key)}
                </button>
              ))}
              <button
                data-testid="mobile-owner-login-button"
                onClick={() => { setMobileOpen(false); setLoginOpen(true); }}
                className="mt-1 flex items-center gap-2 rounded-lg px-3 py-2.5 text-left text-base font-medium text-[#C85A32] hover:bg-stone-100"
              >
                <Lock className="h-4 w-4" /> {t("nav.owner")}
              </button>
            </nav>
          </div>
        )}
      </header>

      <OwnerLoginModal open={loginOpen} onOpenChange={setLoginOpen} />
    </>
  );
};
